import React, { useEffect, useState } from 'react';
import { Box, Flex, Tabs, Skeleton, Stack } from '@mantine/core';
import { IconChevronLeft, IconScript } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

import { DASHBOARD_PATH } from 'routes/paths';
import useLang from 'lang';
import theme from 'theme';
import SimpleLayout from 'layouts/SimpleLayout';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import P2Medium from 'theme/components/Typography/P2Medium';
import DocumentItem from 'components/DocumentItem';
import { useApi } from 'api/api-context';
import panic from 'panic';
import { HelpdeskGetDocumentsResponse } from 'api/actions/helpdesk-get-documents/helpdesk-get-documents-response';

interface Document {
  id: number;
  name: string;
  size: number;
  path: string;
  mimeType: number;
  group: string;
}

/**
 *
 * @param bytes
 * @returns
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

/**
 *
 * @returns
 */
const MyDocuments: React.FC = () => {
  const { _t } = useLang();
  const { getAction } = useApi();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);

  /**
   *
   */
  const loadDocuments = () => {
    const getDocuments = getAction('HelpdeskGetDocuments');

    setLoading(true);
    getDocuments()
      .then((response: HelpdeskGetDocumentsResponse) => {
        const documents = ([...((response?.data as Document[]) ?? [])] as Document[]).sort((left, right) =>
          left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
        );
        setDocuments(documents);
      })
      .catch(panic)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDocuments();
  }, [getAction]);

  return (
    <>
      <SimpleLayout>
        <Box p="1rem">
          <Flex direction="row" align="center">
            <Link
              to={DASHBOARD_PATH.original}
              style={{ textDecoration: 'none', display: 'flex', alignContent: 'center', marginBottom: '0.5rem' }}
            >
              <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
              <P2Medium color={theme.colors?.gray?.[9]}>{_t('Back')}</P2Medium>
            </Link>
          </Flex>
          <Flex gap="0.5rem">
            <IconScript size="2rem" color={theme.colors?.gray?.[12]} />
            <H1SemiBold color={theme.colors?.gray?.[12]}>{_t('My Documents')}</H1SemiBold>
          </Flex>
        </Box>

        <Box>
          <Tabs defaultValue="contracts" variant="pills">
            <Tabs.List px="1rem" style={{ overflowX: 'scroll', flexWrap: 'nowrap' }} display="none">
              <Tabs.Tab value="contracts">{_t('My documents')}</Tabs.Tab>
            </Tabs.List>

            {/* Contracts Tab */}
            <Tabs.Panel value="contracts" p="1rem">
              {loading ? (
                <Stack>
                  <Skeleton height={60} radius="md" />
                  <Skeleton height={60} radius="md" />
                </Stack>
              ) : (
                documents.map((doc: Document) => (
                  <DocumentItem
                    key={doc.id}
                    id={doc.id}
                    title={doc.name}
                    size={formatFileSize(doc.size)}
                    path={doc.path}
                    type={String(doc.mimeType)}
                    badges={[
                      { variant: doc.group === 'private' ? 'red' : 'blue', text: doc.group },
                      { variant: 'white', text: String(doc.mimeType) },
                    ]}
                  />
                ))
              )}
            </Tabs.Panel>
          </Tabs>
        </Box>
      </SimpleLayout>
    </>
  );
};

export default MyDocuments;
